/* eslint-disable max-lines */
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {StoreHelpers} from "react-joyride";
import {reverse} from "../../routing";
import ReactTour, {TourStep} from "../../components/ReactTour";
import {queryData} from "./index.graphql";
import {createDummyProject, createDummyChallenge} from "../../dummies";
import {getLanguageCode} from "../../i18n";
import {useTour} from "../../providers/tour";
import {Project, Challenge, ChallengeMode} from "../../schema";
import {getTargetsClassNamesWithPrefix, TourMock, targetClassNamesToSelectors, getTourSubname} from "../../tours";

export const NAME: "editorIndex" = "editorIndex";
export const SUBNAMES = {
  openChallenge: getTourSubname(NAME, "openChallenge"),
};

type TargetNames = [
  "addChallengeButton",
  "addProjectButton",
  "challengeTable",
  "challengeTableHeader",
  "challenge",
  "challengeItem",
  "challengeDropdown",
  "challengeDropdownContent",
  "filterForm",
  "project",
  "projectDropdown",
  "projectItem",
  "projectDropdownContent"
];
type TargetName = TargetNames[number];

const targetNames: TargetNames = [
  "addChallengeButton",
  "addProjectButton",
  "challengeTable",
  "challengeTableHeader",
  "challenge",
  "challengeItem",
  "challengeDropdown",
  "challengeDropdownContent",
  "filterForm",
  "project",
  "projectDropdown",
  "projectItem",
  "projectDropdownContent"
];

export type Targets = Record<TargetName, TourStep["target"]>;

export const classes = getTargetsClassNamesWithPrefix(targetNames, "tour_editor_index_");

type props = {
  loaded: boolean;
  targets?: Partial<Targets>;
};

export default function EditorIndexTour({loaded, targets: componentTargets, ...props}: props) {
  const {t} = useTranslation("tours");
  const {end: endTour, runningTourName, setMock} = useTour();

  const onTourEnd = useCallback(() => {
    return endTour();
  }, [endTour]);

  useEffect(
    () => () => {
      endTour();
    },
    [endTour],
  );

  const [helpers, setHelpers] = useState<StoreHelpers>();

  const targets = useMemo(
    () => ({
      ...targetClassNamesToSelectors(classes),
      ...componentTargets,
    }),
    [componentTargets],
  );

  const getMockBy = useMemo<EditorIndexTourMock["getBy"]>(
    () => ({
      challenge: new Map([
        [
          ids.challenges[0].id,
          {
            classes: {
              row: classes.challenge,
              dropdown: classes.challengeDropdown,
              dropdownContent: classes.challengeDropdownContent,
              item: classes.challengeItem,
            },
            eventHandlers: {
              dropdownClick: helpers?.next,
            },
            href: reverse("editorChallengeEdit", {id: "tour"}),
          },
        ],
      ]),
      project: new Map([[ids.projects[0].id, {
        classes: {
          row: classes.project,
          dropdown: classes.projectDropdown,
          dropdownContent: classes.projectDropdownContent,
          item: classes.projectItem
        },
        eventHandlers: {
          dropdownClick: helpers?.next
        },
        href: reverse("editorProjectEdit", {id: "tour"})
      }]])
    }),
    [helpers],
  );

  useEffect(() => {
    setMock({
      tour: NAME,
      getBy: getMockBy,
    });
  }, [getMockBy, setMock]);

  const steps = useMemo<TourStep[]>(() => {
    switch (runningTourName) {
      case "editorIndex_openChallenge":
        return [
          {
            title: t("editorIndex.backToEditorIndex.title"),
            content: t("editorIndex.backToEditorIndex.content"),
            target: "body",
            placement: "center",
          },
          {
            title: t("editorIndex.editChallenge.title"),
            content: t("editorIndex.editChallenge.content"),
            target: targets.challengeItem,
            placement: "right",
            spotlightClicks: true,
          },
        ];
      case undefined:
        console.error("Tour (editor index) is mounted, but runningTourName is undefined");
      // falls through
      case "editorIndex":
        return [
          {
            title: t("editorIndex.start.title"),
            content: t("editorIndex.start.content"),
            target: "body",
            placement: "center",
          },
          {
            title: t("editorIndex.challengeTable.title"),
            content: t("editorIndex.challengeTable.content"),
            target: targets.challengeTable,
            placement: "right-start",
          },
          {
            title: t("editorIndex.challengeTableHeader.title"),
            content: t("editorIndex.challengeTableHeader.content"),
            target: targets.challengeTableHeader,
            placement: "right",
          },
          {
            title: t("editorIndex.challenge.title"),
            content: t("editorIndex.challenge.content"),
            target: targets.challenge,
            placement: "right",
          },
          {
            title: t("editorIndex.challengeDropdown.title"),
            content: t("editorIndex.challengeDropdown.content"),
            target: targets.challengeDropdown,
            placement: "right",
            disableNextButton: true,
            spotlightClicks: true,
          },
          {
            title: t("editorIndex.challengeDropdownContent.title"),
            content: t("editorIndex.challengeDropdownContent.content"),
            target: targets.challengeDropdownContent,
            placement: "right",
          },
          {
            title: t("editorIndex.project.title"),
            content: t("editorIndex.project.content"),
            target: targets.project,
            placement: "right",
          },
          {
            title: t("editorIndex.projectDropdown.title"),
            content: t("editorIndex.projectDropdown.content"),
            target: targets.projectDropdown,
            placement: "right",
            disableNextButton: true,
            spotlightClicks: true,
          },
          {
            title: t("editorIndex.projectDropdownContent.title"),
            content: t("editorIndex.projectDropdownContent.content"),
            target: targets.projectDropdownContent,
            placement: "right",
          },
          {
            title: t("editorIndex.filterForm.title"),
            content: t("editorIndex.filterForm.content"),
            target: targets.filterForm,
            placement: "left-start",
          },
          {
            title: t("editorIndex.addProject.title"),
            content: t("editorIndex.addProject.content"),
            target: targets.addProjectButton,
            placement: "bottom",
          },
          {
            title: t("editorIndex.addChallenge.title"),
            content: t("editorIndex.addChallenge.content"),
            target: targets.addChallengeButton,
            placement: "bottom",
          },
          {
            title: t("editorIndex.editProject.title"),
            content: t("editorIndex.editProject.content"),
            target: targets.projectItem,
            placement: "right",
            spotlightClicks: true,
          }
        ]
      default:
        throw Error(`Tour (editor index) is mounted, but running unsupported tour name${runningTourName}`);
    }
  }, [t, targets, runningTourName]);

  return (
    <ReactTour
      continuous
      run={loaded}
      steps={steps}
      onTourEnd={onTourEnd}
      getHelpers={setHelpers}
      scrollToFirstStep
      disableOverlayClose
      disableCloseOnEsc
      showSkipButton
      hideCloseButton
    />
  );
}

const ids = {
  challenges: [{id: "editorIndexTour_Challenge_1"}, {id: "editorIndexTour_Challenge_2"}],
  projects: [{
    id: "editorIndexTour_Project_1",
    challenges: [
      {id: "editorIndexTour_Challenge_3"},
      {id: "editorIndexTour_Challenge_4"},
      {id: "editorIndexTour_Challenge_5"}]
  }]
};

export function useMockData(): queryData {
  const {t, i18n} = useTranslation("tours");
  const languageCode = getLanguageCode(i18n.language);

  const challenges = useMemo(
    () => [
      createDummyChallenge({
        name: t("editorIndex.mockData.challenges.1.name"), // "Тест для начинающих волшебников"
        id: ids.challenges[0].id,
        languageCode,
        lastUpdatedAt: Date.UTC(2020, 10, 16, 8, 38) / 1000,
      }),
      createDummyChallenge({
        name: t("editorIndex.mockData.challenges.2.name"), // "Тест по тесту для начинающих волшебников"
        id: ids.challenges[1].id,
        languageCode,
        lastUpdatedAt: Date.UTC(2020, 10, 16, 8, 36) / 1000,
        mode: ChallengeMode.CHOICE_BASED_STUDY
      }),
    ],
    [languageCode, t],
  );

  const projects = useMemo(() => [
    createDummyProject({
      name: t("editorIndex.mockData.project.1.name"), // "Проект продвинутого волшебника"
      id: ids.projects[0].id,
      languageCode,
      lastUpdatedAt: Date.UTC(2021, 8, 16, 15, 28)/1000,
      challenges: [
        createDummyChallenge({
          name: t("editorIndex.mockData.project.1.challenges.1.name"), // "1. Использование волшебной палочки"
          id: ids.projects[0].challenges[0].id,
          languageCode
        }),
        createDummyChallenge({
          name: t("editorIndex.mockData.project.1.challenges.2.name"), // "2. Базовые заклинания"
          id: ids.projects[0].challenges[1].id,
          languageCode
        }),
        createDummyChallenge({
          name: t("editorIndex.mockData.project.1.challenges.3.name"), // "3. Зельеварение"
          id: ids.projects[0].challenges[2].id,
          languageCode
        })
      ]
    })
  ], [languageCode, t]);
  return {
    items: [...challenges, ...projects],
    projects
  };
}

type MockByChallenge = TourMock<"item" | "dropdown" | "dropdownContent" | "row", "dropdownClick"> & {
  href: string;
};

type MockByProject = TourMock<"item" | "dropdown" | "dropdownContent" | "row", "dropdownClick"> & {
  href: string
}

export type EditorIndexTourMock = {
  tour: "editorIndex";
  getBy: {
    challenge: Map<Challenge["id"], MockByChallenge>;
    project: Map<Project["id"], MockByProject>
  };
};

export function isEditorIndexTourMock(mock: any): mock is EditorIndexTourMock {
  return mock?.tour === NAME;
}
